// Pantalla `command="email-otp"`. Port de la rama `when "email-otp"` de
// keycloak.coffee. Form de introducción del código recibido por email.
// Incluye:
//   - Contador resendWaitSeconds que decrementa cada segundo. Arranca con
//     el valor que manda el servidor (emailOtp.resendWaitSeconds: lo que
//     falta desde el último envío), así que un código erróneo no lo
//     reinicia. Mientras > 0, el botón Reenviar está disabled y se
//     muestra "Reenviar código en N seg.". El servidor rechaza igualmente
//     un reenvío antes de tiempo: el contador es solo ayuda visual.
//   - Botón Reenviar oculto cuando emailOtp.resendAllowed es false (la
//     sesión agotó los reenvíos).
//   - Checkbox "No volver a pedir el código en N días" si
//     emailOtp.cookieTtlDays != null.
//   - Tres acciones: login (Iniciar sesión), resend (reenviar),
//     cancel.

import { useEffect, useState } from "react";
import type { KcData } from "../kcData";
import { Input } from "../components/Input";
import { StandardPage } from "../components/StandardPage";
import { msg } from "../i18n";

export interface EmailOtpPageProps {
  data: KcData;
  running: boolean;
  onCustomSubmit: (action: "login" | "resend" | "cancel") => void;
}

interface EmailOtpData {
  userEmail?: string;
  cookieTtlDays?: number | null;
  rememberChecked?: boolean;
  // true cuando esta pantalla es el paso de código del flujo Email OTP
  // Registration: cambia el botón principal a "Continuar".
  isRegistration?: boolean;
  // Segundos que faltan para poder pedir otro código, según el servidor.
  resendWaitSeconds?: number;
  // false cuando la sesión agotó los reenvíos: no se ofrece el botón.
  resendAllowed?: boolean;
}

// Espera por defecto si el servidor no manda la suya (mismo valor que el
// authenticator por defecto).
const DEFAULT_RESEND_WAIT_SECONDS = 120;

function getEmailOtpData(data: KcData): EmailOtpData | undefined {
  return (
    (data.form?.emailOtp as EmailOtpData | undefined) ??
    (data as unknown as { emailOtp?: EmailOtpData }).emailOtp
  );
}

export function EmailOtpPage(props: EmailOtpPageProps) {
  const { data, running, onCustomSubmit } = props;
  const emailOtp = getEmailOtpData(data);
  const initialResendWait =
    emailOtp?.resendWaitSeconds ?? DEFAULT_RESEND_WAIT_SECONDS;
  const resendAllowed = emailOtp?.resendAllowed !== false;
  const [resendWaitSeconds, setResendWaitSeconds] =
    useState(initialResendWait);

  useEffect(() => {
    if (initialResendWait <= 0) return;
    const intervalId = setInterval(() => {
      setResendWaitSeconds((s) => {
        const next = s - 1;
        if (next <= 0) {
          clearInterval(intervalId);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [initialResendWait]);

  const resendLabel =
    resendWaitSeconds > 0
      ? msg("doResendCodeCountdown", String(resendWaitSeconds))
      : msg("doResendCode");

  return (
    <StandardPage page="email-otp" data={data}>
      <div>
        <form
          id="kc-email-otp-form"
          action={data.url!.loginAction}
          method="post"
        >
          {/* Instrucción con el email del usuario en <strong>. Pre-renderizamos
              en la message con un placeholder {0} que sustituimos por el email. */}
          <p
            className="instruction"
            dangerouslySetInnerHTML={{
              __html: msg(
                "emailOtpInstructionHtml",
                emailOtp?.userEmail ?? "",
              ),
            }}
          />
          <div className="form-group">
            <Input
              type="number"
              className="form-control inputNumber"
              id="emailCode"
              name="emailCode"
              placeholder={msg("emailOtpCodePlaceholder")}
              autoFocus
              messagesPerField={data.messagesPerField}
            />
          </div>
          {emailOtp?.cookieTtlDays != null ? (
            <p className="checkbox otpCodeRemember">
              <label>
                <input
                  type="checkbox"
                  id="emailCodeRemember"
                  defaultValue="true"
                  defaultChecked={emailOtp.rememberChecked === true}
                  name="emailCodeRemember"
                />
                {msg(
                  "rememberCodeForDays",
                  String(emailOtp.cookieTtlDays || 30),
                )}
              </label>
            </p>
          ) : null}
          <div className="form-group">
            <button
              type="button"
              className="btn btn-lg btn-block btn-primary"
              disabled={running}
              onClick={() => onCustomSubmit("login")}
            >
              {emailOtp?.isRegistration ? msg("doContinue") : msg("doLogIn")}
            </button>
          </div>
          {resendAllowed ? (
            <div className="form-group">
              <button
                type="button"
                className="btn btn-lg btn-block btn-default"
                disabled={running || resendWaitSeconds > 0}
                onClick={() => onCustomSubmit("resend")}
              >
                {resendLabel}
              </button>
            </div>
          ) : null}
          <div className="form-group">
            <button
              type="button"
              className="btn btn-lg btn-block btn-default"
              disabled={running}
              onClick={() => onCustomSubmit("cancel")}
            >
              {msg("doCancel")}
            </button>
          </div>
        </form>
      </div>
    </StandardPage>
  );
}
